import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ActionIcon, Button, Divider, Menu } from '@mantine/core';
import dayjs from 'dayjs';
import {
  Dots,
  LayersIntersect2,
  Pencil,
  Refresh,
  Trash
} from 'tabler-icons-react';
import { Context as AdminContext } from '../../../providers/AdminContextProvider';
import TableView from '../../common/TableView';
import { useModalState, usePaginationFilter } from '../../../helpers/hooks';
import HeaderView from '../../common/HeaderView';
import { triggerNotification } from '../../../helpers/notification';
import AppStack from '../../common/AppStack';
import {
  currencyFormat,
  formatUtcDate,
  singularPluralFormat
} from '../../../helpers/format';
import AdminProductImportModal from './AdminProductImportModal';
import AppMenu from '../../common/AppMenu';
import { VIEW_ACTIONS_ENUM } from '../../../config/constants';
import ConfirmModal from '../../common/ConfirmModal';
import AppText from '../../common/AppText';
import { EXTERNAL_CONNECTION_CONFIGS } from '../external/config/externalConnectionConfigs';
import AdminProductMergeModal from './AdminProductMergeModal';
import AdminProductSyncModal from './AdminProductSyncModal';
import { PRODUCT_CATEGORY_LIST } from '../../../config/productCategories';
import ResourceSelectModal from '../../common/ResourceSelectModal';

const TABLE_COLUMNS = [
  {
    label: 'Name',
    value: 'name',
    sortable: true
  },
  {
    label: 'Status',
    value: 'status',
    sortable: true
  },
  {
    label: 'Model',
    value: 'model',
    sortable: true
  },
  {
    label: 'Category',
    value: 'category',
    sortable: true
  },
  {
    label: 'Subcategory',
    value: 'product_type',
    sortable: true
  },
  {
    label: 'Price',
    value: 'price',
    sortable: true
  },
  {
    label: 'Markup %',
    value: 'markup',
    sortable: true
  },
  {
    label: 'Markup $',
    value: 'markup_dollar',
    sortable: false
  },
  {
    label: 'Markup price',
    value: 'markup_price',
    sortable: false
  },
  {
    label: 'External ID',
    value: 'externalId',
    sortable: true
  },
  {
    label: 'Provider',
    value: 'provider',
    sortable: true
  },
  {
    label: 'Created At',
    value: 'created',
    sortable: true
  }
];

const AdminProductTable = () => {
  const hasFetched = useRef(false);
  const navigate = useNavigate();
  const {
    state,
    fetchPaginatedEcomExternalProducts,
    deleteEcomExternalProducts
  } = useContext(AdminContext);
  const [selectedRows, setSelectedRows] = useState([]);
  const {
    pageIndex,
    totalCount,
    totalPages,
    totalNoFilterCount,
    data,
    filter,
    loading: dataLoading,
    isDescendingSort,
    onPageChange,
    onFilterChange,
    onSortChange,
    onRefresh,
    onReset
  } = usePaginationFilter(
    { search: '' },
    (newFilter) => {
      setSelectedRows([]);
      fetchPaginatedEcomExternalProducts(newFilter, null, (error) => {
        triggerNotification(error);
      });
    },
    state.paginatedEcomExternalProducts
  );
  const {
    state: modalState,
    onOpenModal,
    onCloseModal,
    onChangeModalLoading
  } = useModalState();

  const loading = !hasFetched.current || dataLoading;

  const selectedProducts = data.filter((f) =>
    selectedRows.includes(f.pkEcomExternalProduct)
  );

  const productFilter = filter
    ? {
        search: filter.search,
        status: filter.status,
        fkEcomCategory: filter.fkEcomCategory
      }
    : null;

  useEffect(() => {
    onReset();
    hasFetched.current = true;
  }, []);

  return (
    <HeaderView
      breadcrumbs={[
        {
          title: 'External products',
          to: '/admin/products'
        }
      ]}
      rightSection={
        !loading && (
          <>
            <Button
              color="dark"
              onClick={() => {
                onOpenModal('sync-products');
              }}
              radius="md"
              size="compact-md"
              style={{ fontSize: 14 }}
              variant="outline"
              visibleFrom="xsm"
            >
              Sync vendors
            </Button>
            <Button
              color="dark"
              onClick={() => {
                onOpenModal('import');
              }}
              radius="md"
              size="compact-md"
              style={{ fontSize: 14 }}
              variant="outline"
              visibleFrom="xsm"
            >
              Import
            </Button>
          </>
        )
      }
    >
      <TableView
        columns={TABLE_COLUMNS}
        emptyMessage="No products found."
        enableInputsWhenLoading
        filters={[
          {
            key: 1,
            label: 'Search',
            placeholder: 'Filter by name',
            value: filter.search || '',
            onChange: (search) =>
              onFilterChange({
                ...filter,
                search
              })
          },
          {
            key: 2,
            label: 'Status',
            type: 'select',
            value: filter.status ?? null,
            placeholder: 'Filter by status',
            searchable: true,
            clearable: true,
            data: [
              {
                label: 'Print ready',
                value: 'print_ready'
              },
              {
                label: 'Action required',
                value: 'action_required'
              }
            ],
            onChange: (status) => onFilterChange({ ...filter, status }, true)
          },
          {
            key: 3,
            label: 'Category',
            type: 'select',
            value: filter.fkEcomCategory ?? null,
            placeholder: 'Filter by category',
            searchable: true,
            clearable: true,
            data: PRODUCT_CATEGORY_LIST.sort((a, b) =>
              a.name.localeCompare(b.name)
            ).map((c) => ({
              label: c.name,
              value: c.value.toString()
            })),
            onChange: (category) =>
              onFilterChange({ ...filter, fkEcomCategory: category }, true)
          }
        ]}
        isDescendingSort={isDescendingSort}
        isLoading={loading}
        noSort
        onChangeSortBy={onSortChange}
        onPageChange={onPageChange}
        onRefresh={onRefresh}
        onSelect={setSelectedRows}
        pageIndex={pageIndex}
        rows={data.map((p) => {
          const anyMissingTemplates = !p.sizeOptions.every((v) =>
            p.ecomExternalProductPrintTemplates.some((t) => t.sizeOption === v)
          );
          const missingTemplateDimensions = p.ecomExternalProductPrintTemplates.some(
            (t) => !t.imageHeightPx || !t.imageWidthPx
          );

          return {
            key: p.pkEcomExternalProduct,
            columns: [
              {
                key: 1,
                label: p.name,
                weight: 500,
                component: Link,
                to: `/admin/products/${p.pkEcomExternalProduct}`,
                subLabel: singularPluralFormat(
                  p.variantCount,
                  'variant',
                  'variants'
                ),
                onSubLabelClick: () =>
                  navigate(
                    `/vendor/products/${p.pkEcomExternalProduct}/variants?from=products`
                  ),
                src: p.previewImage ?? null,
                showImage: true
              },
              {
                key: 2,
                label: p.missingExternalId
                  ? 'Missing External ID'
                  : anyMissingTemplates
                  ? 'Templates missing'
                  : missingTemplateDimensions
                  ? 'Dimensions missing '
                  : 'Print ready',
                color:
                  p.missingExternalId ||
                  anyMissingTemplates ||
                  missingTemplateDimensions
                    ? '#c40000'
                    : 'green'
              },
              {
                key: 3,
                label: p.model || p.description
              },
              {
                key: 4,
                label: p.fkEcomCategory
                  ? PRODUCT_CATEGORY_LIST.find(
                      (c) => c.value.toString() === p.fkEcomCategory.toString()
                    )?.name
                  : ''
              },
              {
                key: 5,
                label: p.productType || ''
              },
              {
                key: 6,
                label:
                  p.maxPrice === p.minPrice
                    ? `${currencyFormat(p.minPrice / 100)}`
                    : `${currencyFormat(p.minPrice / 100)} - ${currencyFormat(
                        p.maxPrice / 100
                      )}`
              },
              {
                key: 7,
                label: p.markupPercentage ? `${p.markupPercentage}%` : ''
              },
              {
                key: 8,
                label: p.markupPercentage
                  ? p.maxMarkupPrice === p.minMarkupPrice
                    ? `${currencyFormat((p.minMarkupPrice - p.minPrice) / 100)}`
                    : `${currencyFormat(
                        (p.minMarkupPrice - p.minPrice) / 100
                      )} - ${currencyFormat(
                        (p.maxMarkupPrice - p.maxPrice) / 100
                      )}`
                  : ''
              },
              {
                key: 9,
                label: p.markupPercentage
                  ? p.maxMarkupPrice === p.minMarkupPrice
                    ? `${currencyFormat(p.minMarkupPrice / 100)}`
                    : `${currencyFormat(
                        p.minMarkupPrice / 100
                      )} - ${currencyFormat(p.maxMarkupPrice / 100)}`
                  : ''
              },
              {
                key: 10,
                label: p.externalId || ''
              },
              {
                key: 11,
                label:
                  EXTERNAL_CONNECTION_CONFIGS.find(
                    (c) =>
                      c.pkEcomExternalConnectionType ===
                      p.fkEcomExternalConnectionType.toString()
                  )?.title || ''
              },
              {
                key: 12,
                label: dayjs(formatUtcDate(p.createdAt)).format('MMM D, YYYY')
              }
            ]
          };
        })}
        selectActionSection={
          <AppMenu
            control={
              <ActionIcon color="#ced4da" size="lg" variant="outline">
                <Dots color="#000" />
              </ActionIcon>
            }
            styles={{ dropdown: { padding: 0 } }}
          >
            <AppStack style={{ padding: 2, gap: 1 }}>
              <Menu.Item
                leftSection={<Pencil size={18} />}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onOpenModal('bulk_edit', selectedProducts);
                }}
                visibleFrom="xsm"
              >
                Bulk edit
              </Menu.Item>
            </AppStack>
            <AppStack style={{ padding: 2, gap: 1 }}>
              <Menu.Item
                leftSection={<LayersIntersect2 size={18} />}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onOpenModal('group_variants', selectedProducts);
                }}
                visibleFrom="xsm"
              >
                Merge variants
              </Menu.Item>
            </AppStack>
            <AppStack style={{ padding: 2, gap: 1 }}>
              <Menu.Item
                leftSection={<Refresh size={18} />}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onOpenModal('sync-products', selectedProducts);
                }}
                visibleFrom="xsm"
              >
                Sync vendors
              </Menu.Item>
            </AppStack>
            <Divider />
            <AppStack style={{ padding: 2, gap: 1 }}>
              <Menu.Item
                leftSection={<Trash size={18} />}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onOpenModal(VIEW_ACTIONS_ENUM.DELETE, [...selectedProducts]);
                }}
                style={{ color: 'red' }}
              >
                Delete products
              </Menu.Item>
            </AppStack>
          </AppMenu>
        }
        selectedRows={selectedProducts.map((p) => p.pkEcomExternalProduct)}
        sortBy={filter.sort || TABLE_COLUMNS[0].value}
        tableTitle="External products"
        totalCount={totalCount}
        totalPages={totalPages}
      />

      <ConfirmModal
        confirmActionColor="red"
        confirmActionText="Yes, delete products"
        isLoading={modalState.loading}
        isOpen={
          modalState.isOpen && modalState.action === VIEW_ACTIONS_ENUM.DELETE
        }
        message={
          <AppStack style={{ gap: 10 }}>
            <AppText style={{ fontSize: 16 }}>
              Are you sure you want to delete{' '}
              <b>
                {modalState.item?.length === 1
                  ? modalState.item[0]?.name
                  : `${modalState.item?.length} products`}
              </b>
              ?
            </AppText>
            <AppText style={{ fontSize: 14, fontWeight: 500 }}>
              This action cannot be undone.
            </AppText>
          </AppStack>
        }
        onCancel={onCloseModal}
        onConfirm={() => {
          onChangeModalLoading(true);
          deleteEcomExternalProducts(
            modalState.item.map((i) => i.pkEcomExternalProduct),
            () => {
              onRefresh();
              triggerNotification('Products deleted.', 'success');
              onCloseModal();
            },
            (error) => {
              triggerNotification(error);
              onChangeModalLoading(false);
            }
          );
        }}
        title={`Delete ${
          modalState.item?.length === 1
            ? modalState.item[0]?.name
            : `${modalState.item?.length} products`
        }?`}
      />

      <AdminProductImportModal
        isOpen={modalState.isOpen && modalState.action === 'import'}
        onClose={onCloseModal}
        onSuccess={() => {
          onCloseModal();
          onRefresh();
        }}
      />

      <AdminProductSyncModal
        ecomExternalProducts={
          modalState.action === 'sync-products' ? modalState.item : null
        }
        isOpen={modalState.isOpen && modalState.action === 'sync-products'}
        onClose={onCloseModal}
        onSuccess={() => {
          onRefresh();
        }}
      />

      <AdminProductMergeModal
        ecomExternalProducts={modalState.item}
        isOpen={modalState.isOpen && modalState.action === 'group_variants'}
        onClose={onCloseModal}
        onSuccess={() => {
          onCloseModal();
          onRefresh();
        }}
      />

      <ResourceSelectModal
        filter={productFilter}
        isOpen={modalState.isOpen && modalState.action === 'bulk_edit'}
        onClose={onCloseModal}
        onSelect={(selectType) => {
          if (selectType === 'all') {
            const params = new URLSearchParams();
            if (productFilter.search) {
              params.append(`search`, productFilter.search);
            }

            if (productFilter.status) {
              params.append(`status`, productFilter.status);
            }

            if (productFilter.fkEcomCategory) {
              params.append(`category`, productFilter.fkEcomCategory);
            }

            const queryString = params.toString();
            navigate(
              `/admin/products/bulk-edit${queryString ? `?${queryString}` : ''}`
            );
          }
          else {
            navigate(
              `/admin/products/bulk-edit?ids=${selectedProducts
                .map((f) => f.pkEcomExternalProduct)
                .join(',')}`
            );
          }
        }}
        selectedIds={selectedProducts.map((f) => f.pkEcomExternalProduct)}
        selectLabel="Edit for"
        selectLabelDescription="Select the products you want to edit."
        size={450}
        title="Bulk edit products?"
        totalItemCount={totalCount}
      />
    </HeaderView>
  );
};

export default AdminProductTable;
